import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import logger from '../services/logger';
import { setPermissionContext } from '../utils/rolePermissions';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [uiPermissions, setUiPermissions] = useState({});
  const [menuItems, setMenuItems] = useState([]);
  const [pagePermissions, setPagePermissions] = useState({});
  const [roleInfo, setRoleInfo] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const loadPermissions = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/roles/my-permissions');
      const data = response.data;

      setPermissions(data.permissions || []);
      setUiPermissions(data.ui_permissions || {});
      setMenuItems(data.menu_items || []);
      setPagePermissions(data.page_permissions || {});
      setRoleInfo(data.role || null);
      setPermissionsLoaded(true);

      logger.debug('Permissions loaded from API:', data.role?.name);
    } catch (error) {
      logger.warn('Could not load permissions from API, using defaults:', error.message);
      setPermissionsLoaded(false);
    }
  }, []);

  const clearPermissions = useCallback(() => {
    setPermissions([]);
    setUiPermissions({});
    setMenuItems([]);
    setPagePermissions({});
    setRoleInfo(null);
    setPermissionsLoaded(false);
  }, []);

  // UI permission check: hasPermission('devices', 'add_device')
  const hasPermission = useCallback((category, action) => {
    if (!permissionsLoaded) return false;
    return uiPermissions[category]?.[action] === true;
  }, [uiPermissions, permissionsLoaded]);

  // Page access check
  const hasPageAccess = useCallback((path) => {
    if (!permissionsLoaded) return false;

    // Direct match
    if (pagePermissions[path] !== undefined) {
      return pagePermissions[path] === true;
    }

    // Dynamic route match (e.g., /devices/123 -> /devices/:id)
    for (const [routePattern, allowed] of Object.entries(pagePermissions)) {
      if (routePattern.includes(':')) {
        const regexPattern = routePattern.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${regexPattern}$`);
        if (regex.test(path)) {
          return allowed === true;
        }
      }
    }

    return false;
  }, [pagePermissions, permissionsLoaded]);

  // Menu access check
  const canAccessMenu = useCallback((menuKey) => {
    if (!permissionsLoaded) return false;
    return menuItems.includes(menuKey);
  }, [menuItems, permissionsLoaded]);

  // Raw permission check: hasRawPermission('device:read')
  const hasRawPermission = useCallback((permissionValue) => {
    return permissions.includes(permissionValue);
  }, [permissions]);

  // Role name check
  const hasRole = useCallback((roleName) => {
    if (!roleInfo) return false;
    const roles = Array.isArray(roleName) ? roleName : [roleName];
    return roles.includes(roleInfo.name);
  }, [roleInfo]);

  // Get role hierarchy level
  const getRoleLevel = useCallback(() => {
    return roleInfo?.hierarchy_level || 0;
  }, [roleInfo]);

  const value = {
    permissions,
    uiPermissions,
    menuItems,
    pagePermissions,
    roleInfo,
    permissionsLoaded,
    loadPermissions,
    clearPermissions,
    hasPermission,
    hasPageAccess,
    canAccessMenu,
    hasRawPermission,
    hasRole,
    getRoleLevel,
  };

  // rolePermissions.js helper fonksiyonlarına context referansı ver
  useEffect(() => {
    setPermissionContext(value);
  }, [permissionsLoaded, permissions, uiPermissions, menuItems, pagePermissions, roleInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
